import * as Sentry from '@sentry/react';
import { getHostnameWithEndpoint } from '@utils/helpers';
const TIMEOUT_MS = 30000; // 30 seconds
// GraphQL query for svc_lookup
const SVC_LOOKUP_QUERY = `
  query lookup($hostname: String!) {
    svc_lookup(hostname: $hostname, application: SUPERGRAPH) {
      domain
    }
  }
`;
// GraphQL query for svc_config
const SVC_CONFIG_QUERY = `
  query getConfig {
    svc_config(application: CANDIDATE_EXPERIENCE) {
      application
      config {
        key
        value
      }
    }
  }
`;
// Default fallback configuration
// Uses process.env values if available, otherwise falls back to production defaults
const DEFAULT_FALLBACK_CONFIG = {
    HASURA_HTTP: 'https://supergraph.talview.com',
    HASURA_WS: 'wss://supergraph.talview.com',
    FIREBASE_API_DOMAIN: 'top-prod-363907.firebaseapp.com',
    FIREBASE_API_KEY: 'AIzaSyDA7-lS1xZLVOmpt3phVn_Y5E-IL4kFoFM',
    FORM_APP_DOMAIN: process.env.FORM_APP_DOMAIN || 'https://talview.app',
    GROWTHBOOK_CLIENT_KEY: process.env.GROWTHBOOK_CLIENT_KEY,
    GROWTHBOOK_API_HOST: process.env.GROWTHBOOK_API_HOST || 'https://cdn.growthbook.io',
    MIXPANEL_TOKEN: process.env.MIXPANEL_TOKEN,
    CHROMATIC_PROJECT_TOKEN: process.env.CHROMATIC_PROJECT_TOKEN,
    CODECOV_TOKEN: process.env.CODECOV_TOKEN,
    OLD_CANDIDATE_X_URL: process.env.OLD_CANDIDATE_X_URL || 'https://cx.talview.com',
    CANDIDATE_X_URL: process.env.CANDIDATE_X_URL || 'https://cx-v2.talview.com',
    PADDLE_CLIENT_TOKEN: process.env.PADDLE_CLIENT_TOKEN,
    CODE_SOLO_BASE_DOMAIN: process.env.CODE_SOLO_BASE_DOMAIN || 'https://code-solo.talview.com',
    SECONDARY_HASURA_HTTP: process.env.SECONDARY_HASURA_HTTP || 'https://graphql.talview.com',
    TZ: process.env.TZ || 'UTC',
};
/**
 * Fetch GraphQL query using native fetch
 * @param query - GraphQL query string
 * @param variables - Query variables
 * @param endpoint - GraphQL endpoint URL
 * @returns Promise with query result
 */
const fetchGraphQL = async (query, variables, endpoint) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ query, variables }),
            signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        if (result.errors) {
            throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
        }
        return result.data;
    }
    catch (error) {
        clearTimeout(timeoutId);
        throw error;
    }
};
/**
 * Convert HTTP URL to WebSocket URL
 * @param httpUrl - HTTP URL (e.g., https://example.com/v1/graphql)
 * @returns WebSocket URL (e.g., wss://example.com/v1/graphql)
 */
const convertToWebSocketUrl = (httpUrl) => httpUrl.replace(/^https?:\/\//, 'wss://');
/**
 * Fetch domain from svc_lookup query
 * @param {string} hostname - Current hostname from window.location
 * @param {string} endpoint - GraphQL endpoint URL
 * @returns Promise with domain URL
 */
const fetchDomain = async (hostname, endpoint) => {
    try {
        console.log(`[RuntimeConfig] Fetching domain for hostname: ${hostname}`);
        const data = await fetchGraphQL(SVC_LOOKUP_QUERY, { hostname }, endpoint);
        if (!data?.svc_lookup?.domain) {
            throw new Error('Invalid response from svc_lookup: missing domain');
        }
        const domain = data.svc_lookup.domain;
        // Validate that domain is a valid URL
        // try {
        //   new URL(domain);
        // } catch (urlError) {
        //   throw new Error(`Invalid domain URL returned from svc_lookup: ${domain}`);
        // }
        // Add https:// only if the domain doesn't already have a protocol
        const httpUrl = domain.startsWith('http://') || domain.startsWith('https://') ? domain : `https://${domain}`;
        const wsUrl = convertToWebSocketUrl(httpUrl);
        return { httpUrl, wsUrl };
    }
    catch (error) {
        console.error('[RuntimeConfig] Error fetching domain:', error);
        // Log to Sentry
        const sentryError = new Error(`Failed to fetch domain from svc_lookup for hostname: ${hostname}`);
        Sentry.captureException(sentryError, {
            extra: {
                hostname,
                originalError: error instanceof Error ? error.message : String(error),
            },
        });
        throw error;
    }
};
/**
 * Fetch configuration from svc_config query
 * @param hasuraHttp - HASURA HTTP endpoint to use for the query
 * @returns Promise with configuration object
 */
const fetchConfig = async (hasuraHttp) => {
    try {
        console.log('[RuntimeConfig] Fetching configuration from svc_config');
        const data = await fetchGraphQL(SVC_CONFIG_QUERY, {}, hasuraHttp);
        if (!data?.svc_config?.config) {
            throw new Error('Invalid response from svc_config: missing config array');
        }
        // Convert array of {key, value} to object
        const configObj = {};
        data.svc_config.config.forEach(({ key, value }) => {
            // Ignore HASURA_HTTP and HASURA_WS from svc_config as per requirement
            if (key !== 'HASURA_HTTP' && key !== 'HASURA_WS') {
                configObj[key] = value;
            }
        });
        return configObj;
    }
    catch (error) {
        console.error('[RuntimeConfig] Error fetching config:', error);
        // Log to Sentry
        const sentryError = new Error('Failed to fetch configuration from svc_config');
        Sentry.captureException(sentryError, {
            extra: {
                hasuraHttp,
                originalError: error instanceof Error ? error.message : String(error),
            },
        });
        throw error;
    }
};
/**
 * Get fallback configuration
 * @returns Fallback configuration object
 */
const getFallbackConfig = () => {
    console.log('[RuntimeConfig] Using fallback configuration');
    return { ...DEFAULT_FALLBACK_CONFIG };
};
/**
 * Initialize runtime configuration
 * This is the main export that should be called during app initialization
 * @returns Promise with runtime configuration
 */
export const initializeRuntimeConfig = async () => {
    try {
        const { hostname, endpoint } = getHostnameWithEndpoint();
        if (!hostname) {
            console.warn('[RuntimeConfig] Unable to determine hostname, using fallback configuration');
            return getFallbackConfig();
        }
        let hasuraHttp;
        let hasuraWs;
        // Step 1: Fetch domain from svc_lookup
        try {
            const domainResult = await fetchDomain(hostname, endpoint);
            hasuraHttp = domainResult.httpUrl;
            hasuraWs = domainResult.wsUrl;
        }
        catch (error) {
            console.warn('[RuntimeConfig] svc_lookup failed, using fallback URLs');
            hasuraHttp = DEFAULT_FALLBACK_CONFIG.HASURA_HTTP;
            hasuraWs = DEFAULT_FALLBACK_CONFIG.HASURA_WS;
        }
        // Step 2: Fetch additional config from svc_config
        let additionalConfig = {};
        try {
            const graphql_url = `${hasuraHttp}/v1/graphql`;
            additionalConfig = await fetchConfig(graphql_url);
        }
        catch (error) {
            console.warn('[RuntimeConfig] svc_config failed, using fallback config values including HASURA URLs');
            // Use fallback for additional config and also reset HASURA URLs to defaults
            const { HASURA_HTTP, HASURA_WS, ...fallbackAdditionalConfig } = DEFAULT_FALLBACK_CONFIG;
            additionalConfig = fallbackAdditionalConfig;
            // Reset HASURA URLs to defaults since fetchConfig failed
            hasuraHttp = DEFAULT_FALLBACK_CONFIG.HASURA_HTTP;
            hasuraWs = DEFAULT_FALLBACK_CONFIG.HASURA_WS;
        }
        // Step 3: Merge configurations
        const finalConfig = {
            ...additionalConfig,
            HASURA_HTTP: hasuraHttp,
            HASURA_WS: hasuraWs,
            FIREBASE_API_DOMAIN: additionalConfig.FIREBASE_API_DOMAIN ?? DEFAULT_FALLBACK_CONFIG.FIREBASE_API_DOMAIN,
            FIREBASE_API_KEY: additionalConfig.FIREBASE_API_KEY ?? DEFAULT_FALLBACK_CONFIG.FIREBASE_API_KEY,
            CANDIDATE_X_URL: additionalConfig.CANDIDATE_X_URL ?? DEFAULT_FALLBACK_CONFIG.CANDIDATE_X_URL,
        };
        return finalConfig;
    }
    catch (error) {
        console.error('[RuntimeConfig] Unexpected error during initialization:', error);
        Sentry.captureException(error);
        // Return complete fallback configuration
        return getFallbackConfig();
    }
};
/**
 * Get runtime configuration URLs
 * This is a convenience export for testing/debugging
 * @returns Promise with runtime configuration
 */
export const getRuntimeConfig = initializeRuntimeConfig;
/**
 * Module-level cached runtime configuration
 * This is set by RuntimeConfigProvider after fetching config
 * Used by non-React code (utilities, sagas, helpers) that can't use hooks
 */
let cachedRuntimeConfig = null;
/**
 * Set the module-level runtime configuration
 * Called by RuntimeConfigProvider after config is fetched
 * @param config - Runtime configuration object
 */
export const setRuntimeConfig = (config) => {
    cachedRuntimeConfig = config;
    console.log('[RuntimeConfig] Module-level config cached for non-React code');
};
/**
 * Get the module-level runtime configuration synchronously
 * For use in non-React code (utilities, sagas, helpers)
 * @throws Error if config is not initialized
 * @returns RuntimeConfig object
 */
export const getRuntimeConfigSync = () => {
    if (!cachedRuntimeConfig) {
        throw new Error('Runtime configuration not initialized. Make sure RuntimeConfigProvider is mounted and initialized.');
    }
    return cachedRuntimeConfig;
};
